<template>
    <div>
        <div v-if="!loading && !ruralProducer" class="ui-suggestion ui-suggestion--warn mb-4">
            Este caso não está marcado como produtor rural. Marque em Configuração para começar com as perguntas do questionário modelo
            (ou cadastre as perguntas deste caso aqui mesmo).
        </div>

        <v-row>
            <v-col cols="12" md="6">
                <ui-section title="Perguntas do caso" :description="questionsDescription" flush>
                    <div class="rma-qtab-toolbar">
                        <v-btn v-if="$allowed('rma.questionnaire')" small color="primary" depressed :disabled="busy" @click="openEdit(null)"><v-icon left small>mdi-plus</v-icon>Nova pergunta</v-btn>
                        <v-btn small text color="primary" :disabled="busy || !catalogCount" @click="importCatalog"><v-icon left small>mdi-tray-arrow-down</v-icon>Trazer do modelo</v-btn>
                    </div>
                    <div v-if="loading" class="ui-small ui-muted pa-4">Carregando...</div>
                    <rma-question-list v-else :questions="questions" :busy="busy" empty-text="Nenhuma pergunta neste caso."
                        @edit="openEdit" @remove="removeQuestion" @reorder="reorder" />
                </ui-section>
            </v-col>

            <v-col cols="12" md="6">
                <ui-section title="Envios" description="Cada envio vai por e-mail à recuperanda, que responde no Painel da Devedora." flush>
                    <div class="rma-qtab-toolbar">
                        <v-btn v-if="$allowed('rma.questionnaire')" small color="primary" depressed :disabled="!questions.length" @click="openSend">
                            <v-icon left small>mdi-send-outline</v-icon>Enviar questionário
                        </v-btn>
                    </div>
                    <div v-if="!questionnaires.length" class="ui-small ui-muted pa-4">Nenhum questionário enviado.</div>
                    <button v-for="row in questionnaires" :key="row.id" type="button" class="rma-qn" @click="openDetail(row.id)">
                        <div class="rma-qn__main">
                            <div class="ui-cell-main text-truncate">{{ row.title }}</div>
                            <div class="ui-cell-sub">
                                Enviado em {{ formatDateTime(row.sent_at) }}<span v-if="row.sent_by_name"> por {{ row.sent_by_name }}</span>
                                <span v-if="row.due_date_label"> · prazo {{ row.due_date_label }}</span>
                                · {{ row.questions }} pergunta(s)
                            </div>
                            <div v-if="row.answered_at" class="ui-cell-sub">Respondido em {{ formatDateTime(row.answered_at) }}<span v-if="row.answered_by_name"> por {{ row.answered_by_name }}</span></div>
                        </div>
                        <ui-badge :status="badge(row.status)" :text="row.status_label" />
                        <v-icon small class="ui-cell-chevron">mdi-chevron-right</v-icon>
                    </button>
                </ui-section>
            </v-col>
        </v-row>

        <rma-question-dialog v-model="edit.open" :question="edit.question" :answer-types="answerTypes" :saving="busy"
            description="Vale só para este caso. Questionários já enviados não mudam." @save="saveQuestion" />

        <v-dialog v-model="send.open" max-width="560" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Enviar questionário</h3>
                        <p class="ui-section-description">Vai com as {{ questions.length }} pergunta(s) atuais do caso. A recuperanda recebe por e-mail e responde no Painel da Devedora.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-text-field v-model="send.title" label="Título" outlined dense hide-details="auto" maxlength="255" class="mb-3"></v-text-field>
                    <v-textarea v-model="send.message" label="Mensagem (opcional)" outlined rows="3" auto-grow hide-details="auto" maxlength="4000" class="mb-3"></v-textarea>
                    <v-text-field v-model="send.due_date" type="date" label="Prazo para responder (opcional)" outlined dense hide-details="auto" :min="today"></v-text-field>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="send.saving" @click="send.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('rma.questionnaire')" color="primary" depressed :loading="send.saving" @click="confirmSend"><v-icon left small>mdi-send-outline</v-icon>Enviar</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="detail.open" max-width="760" scrollable>
            <v-card v-if="detail.data" class="ui-card">
                <div class="ui-card__header">
                    <div style="min-width: 0;">
                        <h3 class="ui-section-title">{{ detail.data.title }}</h3>
                        <p class="ui-section-description">
                            Enviado em {{ formatDateTime(detail.data.sent_at) }}<span v-if="detail.data.due_date_label"> · prazo {{ detail.data.due_date_label }}</span>
                            <span v-if="detail.data.answered_at"> · respondido em {{ formatDateTime(detail.data.answered_at) }}<span v-if="detail.data.answered_by_name"> por {{ detail.data.answered_by_name }}</span></span>
                        </p>
                    </div>
                    <ui-badge :status="badge(detail.data.status)" :text="detail.data.status_label" />
                </div>
                <v-card-text class="ui-card__body">
                    <div v-if="detail.data.message" class="ui-suggestion mb-3">{{ detail.data.message }}</div>
                    <div v-for="(item, index) in detail.data.items" :key="item.id" class="rma-answer">
                        <div class="rma-answer__question">{{ index + 1 }}. {{ item.text }}</div>
                        <div class="rma-answer__value" :class="{ 'ui-muted': formatAnswer(item) === null }">
                            {{ formatAnswer(item) === null ? (detail.data.status === "sent" ? "Aguardando resposta" : "Sem resposta") : formatAnswer(item) }}
                        </div>
                    </div>
                </v-card-text>
                <div class="ui-card__footer">
                    <v-btn v-if="(detail.data.status !== 'closed') && $allowed('rma.questionnaire')" outlined :loading="detail.closing" @click="closeQuestionnaire">Encerrar</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" depressed @click="detail.open = false">Fechar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import RmaQuestionDialog from "@/components/rma/RmaQuestionDialog.vue";
import RmaQuestionList from "@/components/rma/RmaQuestionList.vue";
import { formatDate, formatDateTime, notifyError, notifySuccess, notifyWarning } from "@/components/rma/rma.util";

// Aba Questionario do caso no RMA: perguntas do caso (comecam com o modelo),
// envios a recuperanda e respostas.
export default {
    name: "RmaQuestionnaireTab",

    components: { RmaQuestionDialog, RmaQuestionList },

    props: {
        clientId: { type: Number, required: true },
        // Abre direto um envio (link do e-mail de resposta).
        openId: { type: Number, default: null },
    },

    data: () => ({
        loading: false,
        busy: false,
        ruralProducer: false,
        questions: [],
        catalogCount: 0,
        answerTypes: [],
        questionnaires: [],
        edit: { open: false, question: null },
        send: { open: false, title: "", message: "", due_date: "", saving: false },
        detail: { open: false, data: null, closing: false },
    }),

    computed: {
        questionsDescription() {
            return `${this.questions.length} pergunta(s). Começam com o questionário modelo; ajustes valem só para este caso.`;
        },
        today() {
            const now = new Date();
            return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
        },
    },

    async created() {
        await this.load();
        if (this.openId) this.openDetail(this.openId);
    },

    methods: {
        formatDateTime,

        badge(status) {
            if (status === "sent") return "pending";
            if (status === "answered") return "active";
            return "inactive";
        },

        applyQuestions(content) {
            this.ruralProducer = !!content.rural_producer;
            this.questions = content.questions || [];
            this.catalogCount = content.catalog_count || 0;
            this.answerTypes = content.answer_types || [];
        },

        async load() {
            this.loading = true;
            try {
                const [questions, questionnaires] = await Promise.all([
                    axios.get(`/api/admin/rma/clients/${this.clientId}/questions`),
                    axios.get(`/api/admin/rma/clients/${this.clientId}/questionnaires`),
                ]);
                this.applyQuestions(questions.data.content);
                this.questionnaires = questionnaires.data.content || [];
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },

        async run(request) {
            this.busy = true;
            try {
                this.applyQuestions((await request()).data.content);
                return true;
            } catch (error) {
                notifyError(this, error);
                return false;
            } finally {
                this.busy = false;
            }
        },

        openEdit(question) {
            this.edit = { open: true, question };
        },

        async saveQuestion(payload) {
            const base = `/api/admin/rma/clients/${this.clientId}/questions`;
            const ok = await this.run(() => (payload.id ? axios.put(`${base}/${payload.id}`, payload) : axios.post(base, payload)));
            if (ok) {
                this.edit.open = false;
                notifySuccess(this, "Pergunta salva.");
            }
        },

        async removeQuestion(question) {
            const confirmed = await this.$dialog.confirm({
                title: "Excluir pergunta",
                text: `"${question.text}" sai das perguntas deste caso. Questionários já enviados não mudam. Confirma?`,
                actions: { false: "Cancelar", true: { text: "Excluir", color: "error" } },
            });
            if (confirmed) await this.run(() => axios.delete(`/api/admin/rma/clients/${this.clientId}/questions/${question.id}`));
        },

        reorder(ids) {
            return this.run(() => axios.post(`/api/admin/rma/clients/${this.clientId}/questions/reorder`, { ids }));
        },

        async importCatalog() {
            const before = this.questions.length;
            const ok = await this.run(() => axios.post(`/api/admin/rma/clients/${this.clientId}/questions/import`));
            if (ok) notifySuccess(this, this.questions.length > before ? `${this.questions.length - before} pergunta(s) trazida(s) do modelo.` : "O caso já tem todas as perguntas do modelo.");
        },

        openSend() {
            this.send = { open: true, title: `Questionário de fiscalização · ${formatDate(this.today)}`, message: "", due_date: "", saving: false };
        },

        async confirmSend() {
            this.send.saving = true;
            try {
                const result = await axios.post(`/api/admin/rma/clients/${this.clientId}/questionnaires`, {
                    title: this.send.title, message: this.send.message, due_date: this.send.due_date || null,
                });
                const email = result.data.content.email || {};
                this.send.open = false;
                if (email.no_recipients) notifyWarning(this, "Questionário registrado, mas não há usuário da recuperanda para receber o e-mail.");
                else if (!email.sent) notifyWarning(this, "Questionário registrado, mas o e-mail não foi entregue.");
                else notifySuccess(this, "Questionário enviado à recuperanda.");
                this.questionnaires = (await axios.get(`/api/admin/rma/clients/${this.clientId}/questionnaires`)).data.content || [];
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.send.saving = false;
            }
        },

        async openDetail(id) {
            try {
                const result = await axios.get(`/api/admin/rma/questionnaires/${id}`);
                this.detail = { open: true, data: result.data.content, closing: false };
            } catch (error) {
                notifyError(this, error);
            }
        },

        async closeQuestionnaire() {
            this.detail.closing = true;
            try {
                const result = await axios.post(`/api/admin/rma/questionnaires/${this.detail.data.id}/close`);
                this.detail.data = result.data.content;
                this.questionnaires = (await axios.get(`/api/admin/rma/clients/${this.clientId}/questionnaires`)).data.content || [];
                notifySuccess(this, "Questionário encerrado.");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.detail.closing = false;
            }
        },

        formatAnswer(item) {
            const value = item.answer;
            if (value === null || value === undefined || value === "" || (Array.isArray(value) && !value.length)) return null;
            switch (item.answer_type) {
                case "yes_no": return value === "sim" ? "Sim" : "Não";
                case "multiple_choice": return value.join(", ");
                case "currency": return Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
                case "number": return Number(value).toLocaleString("pt-BR");
                case "date": return formatDate(value);
                default: return String(value);
            }
        },
    },
};
</script>

<style scoped>
.rma-qtab-toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 16px;
    border-bottom: 1px solid #eef2f6;
    background: #fafbfc;
}

.rma-qn {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 16px;
    border: 0;
    border-top: 1px solid #eef2f6;
    background: transparent;
    text-align: left;
    font: inherit;
    color: inherit;
    cursor: pointer;
}

.rma-qn:first-of-type {
    border-top: 0;
}

.rma-qn:hover {
    background: #f8fafc;
}

.rma-qn__main {
    flex: 1;
    min-width: 0;
}

.rma-answer {
    padding: 10px 0;
    border-top: 1px solid #eef2f6;
}

.rma-answer:first-of-type {
    border-top: 0;
}

.rma-answer__question {
    font-weight: 600;
    font-size: 13.5px;
}

.rma-answer__value {
    margin-top: 4px;
    font-size: 14px;
    white-space: pre-line;
}
</style>
